import React, { useState, useEffect, useMemo } from 'react';
import { ComplaintWizardState } from '../NewComplaintModal';
import ItemCard from '../Shared/ItemCard/ItemCard';
import { fetchOrderItems, OrderItemDetails, ComplaintItem } from '../../services/complaintsAPI';
import styles from './ItemSelectionStep.module.css';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useNotificationToasts } from '../../../../hooks/useNotificationToasts';
import { NotificationToastContainer } from '../../../../components/NotificationToast/NotificationToast';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';

interface ItemSelectionStepProps {
    data: ComplaintWizardState;
    updateData: (updates: Partial<ComplaintWizardState>) => void;
    goToStep?: (step: number) => void;
}

const ItemSelectionStep: React.FC<ItemSelectionStepProps> = ({ data, updateData, goToStep }) => {
    const [availableProducts, setAvailableProducts] = useState<OrderItemDetails[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // Form State
    const [selectedProduct, setSelectedProduct] = useState<OrderItemDetails | null>(null);
    const [isBoxSelected, setIsBoxSelected] = useState(false);
    const [batch, setBatch] = useState<string[]>(['']);
    const [observation, setObservation] = useState('');
    const [quantities, setQuantities] = useState<{ [size: string]: number }>({});
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [focusedSize, setFocusedSize] = useState<string | null>(null);
    const [isBatchFocused, setIsBatchFocused] = useState(false);
    const [showExceededWarning, setShowExceededWarning] = useState(false);

    const refundOptions = [
        { value: 'CREDITO', label: 'Gerar crédito' },
        { value: 'DESCONTO', label: 'Desconto no boleto' },
        { value: 'REPOSICAO', label: 'Reposição' }
    ];
    const currentRefundLabel = refundOptions.find(o => o.value === data.tipoRessarcimento)?.label || 'Selecione...';

    // Hooks
    const { addToast, toasts, removeToast } = useNotificationToasts();

    // Memoized calculation of quantities already claimed for this product in other items
    const otherItemsClaimed = useMemo(() => {
        if (!selectedProduct) return { sizes: {} as { [size: string]: number }, total: 0 };

        const otherItems = data.items.filter(item => item.id !== editingItemId && item.productRef === selectedProduct.reference);

        const sizeTotals: { [size: string]: number } = {};
        let grandTotal = 0;

        otherItems.forEach(item => {
            Object.entries(item.quantities).forEach(([size, qty]) => {
                sizeTotals[size] = (sizeTotals[size] || 0) + qty;
                grandTotal += qty;
            });
        });

        return { sizes: sizeTotals, total: grandTotal };
    }, [selectedProduct, data.items, editingItemId]);

    // Automatic error detection (Responsiveness)
    const errors = useMemo(() => {
        const newErrors: { [size: string]: boolean } = {};
        if (!selectedProduct) return newErrors;

        Object.entries(quantities).forEach(([size, qty]) => {
            const purchased = selectedProduct.sizes[size] || 0;
            const alreadyClaimed = otherItemsClaimed.sizes[size] || 0;
            if (qty > (purchased - alreadyClaimed)) {
                newErrors[size] = true;
            }
        });
        return newErrors;
    }, [quantities, selectedProduct, otherItemsClaimed]);

    const currentTotal = isBoxSelected
        ? (quantities['volume'] || 0)
        : Object.values(quantities).reduce((a: number, b: number) => a + b, 0);
    const totalExceeded = useMemo(() => {
        if (!selectedProduct) return false;
        const totalAvailable = selectedProduct.totalPurchased - otherItemsClaimed.total;
        return currentTotal > totalAvailable;
    }, [currentTotal, selectedProduct, otherItemsClaimed]);

    // Auto-hide warning if errors are fixed
    useEffect(() => {
        const hasErrors = Object.values(errors).some(e => e) || totalExceeded;
        if (!hasErrors && showExceededWarning) {
            setShowExceededWarning(false);
        }
    }, [errors, totalExceeded, showExceededWarning]);

    useEffect(() => {
        const loadProducts = async () => {
            if (data.order?.id) {
                setLoadingProducts(true);
                try {
                    const items = await fetchOrderItems(data.order.id);
                    setAvailableProducts(items);
                } catch (err) {
                    console.error(err);
                } finally {
                    setLoadingProducts(false);
                }
            }
        };
        loadProducts();
    }, [data.order?.id]);

    const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;

        if (value === 'BOX') {
            setSelectedProduct(null);
            setIsBoxSelected(true);
            setQuantities({ 'volume': 0 });
            setBatch(['']);
            setObservation('');
        } else {
            const productId = Number(value);
            const product = availableProducts.find(p => p.productId === productId);
            setSelectedProduct(product || null);
            setIsBoxSelected(false);
            setQuantities({});
            setBatch(['']);
            setObservation('');
        }
    };

    const handleClearProduct = () => {
        if (editingItemId) {
            resetForm();
        } else {
            setSelectedProduct(null);
            setIsBoxSelected(false);
            setQuantities({});
            setBatch(['']);
            setObservation('');
        }
    };

    // Prepara as opções para o CustomSelect
    const productOptions = [
        { value: 'BOX', label: 'CAIXA (EMBALAGEM)' },
        ...availableProducts.map(p => ({
            value: p.productId,
            label: `${p.reference} - ${p.description}${p.ca ? ` (C.A. ${p.ca})` : ''}`,
        }))
    ];

    const handleQuantityChange = (size: string, value: string) => {
        const numValue = parseInt(value.replace(/\D/g, '')) || 0;
        const maxQtyForSize = (selectedProduct?.sizes[size] || 0) - (otherItemsClaimed.sizes[size] || 0);

        if (numValue > maxQtyForSize) {
            if (!errors[size]) {
                const purchased = selectedProduct?.sizes[size] || 0;
                const alreadyClaimed = otherItemsClaimed.sizes[size] || 0;

                addToast({
                    id: Date.now().toString(),
                    type: 'alert',
                    title: 'Quantidade Excedida',
                    description: alreadyClaimed > 0
                        ? `Você já adicionou ${alreadyClaimed} pares do tamanho ${size}. Restam apenas ${maxQtyForSize} de ${purchased} comprados.`
                        : `Você informou ${numValue} pares para o tamanho ${size}, mas o cliente comprou apenas ${purchased}.`,
                    time: 'Agora',
                    read: false
                });
            }
        }

        setQuantities(prev => ({
            ...prev,
            [size]: numValue
        }));
    };

    const handleBatchChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        // Limpa tudo que não é número ou ponto
        const numbers = value.replace(/\D/g, '');

        // Formata como: MM.YY
        let formatted = '';
        if (numbers.length > 0) {
            // Mês (primeiros 2 dígitos)
            let month = numbers.substring(0, 2);
            if (month.length === 1 && parseInt(month) > 1) month = '0' + month;
            if (month.length === 2 && (parseInt(month) < 1 || parseInt(month) > 12)) month = '12';

            formatted += month;

            if (numbers.length > 2) {
                // Ano (próximos 2 dígitos)
                formatted += '.' + numbers.substring(2, 4);
            }
        }

        setBatch(prev => {
            const newBatch = [...prev];
            newBatch[index] = formatted;
            return newBatch;
        });
    };

    const addBatchField = () => {
        if (batch.length >= 10) {
            addToast({
                id: 'batch-limit-exceeded',
                type: 'alert',
                title: 'Limite de Lotes Excedido',
                description: 'O limite máximo é de 10 lotes por item.',
                time: 'Agora',
                read: false
            });
            return;
        }

        setBatch(prev => [...prev, '']);
    };

    const canAddMoreBatches = () => {
        return batch.length < 10;
    };

    const removeBatch = (index: number) => {
        setBatch(prev => prev.filter((_, i) => i !== index));
    };

    const validateBatch = (value: string): boolean => {
        // Formato esperado: "MM.YY" onde MM é 01-12 e YY são 2 dígitos
        const regex = /^(0[1-9]|1[0-2])\.\d{2}$/;
        return regex.test(value);
    };

    const handleSaveItem = () => {
        // Validação comum: Lote (apenas para calçados)
        const validBatches = batch.filter(b => b && b.trim() !== '');
        if (!isBoxSelected && validBatches.length === 0) {
            addToast({
                id: 'batch-empty',
                type: 'alert',
                title: 'Lote Obrigatório',
                description: 'Por favor, informe pelo menos um Lote / Fabricação do produto.',
                time: 'Agora',
                read: false
            });
            const batchInput = document.querySelector('input[name="batch-input"]') as HTMLInputElement;
            if (batchInput) {
                batchInput.focus();
                batchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (!isBoxSelected && validBatches.some(b => !validateBatch(b))) {
            addToast({
                id: 'batch-invalid',
                type: 'alert',
                title: 'Formato de Lote Inválido',
                description: 'Todos os lotes devem estar no formato MM.YY (Ex: 03.26).',
                time: 'Agora',
                read: false
            });
            const batchInput = document.querySelector('input[name="batch-input"]') as HTMLInputElement;
            if (batchInput) {
                batchInput.focus();
                batchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Validações específicas por tipo
        if (isBoxSelected) {
            // Apenas caixa: valida volume
            if (!quantities['volume'] || quantities['volume'] <= 0) {
                addToast({
                    id: 'volume-empty',
                    type: 'alert',
                    title: 'Volume Obrigatório',
                    description: 'Informe a quantidade de caixas para a reclamação.',
                    time: 'Agora',
                    read: false
                });
                const volumeInput = document.querySelector('input[name="volume-input"]') as HTMLInputElement;
                if (volumeInput) {
                    volumeInput.focus();
                    volumeInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }
        } else {
            // Calçado: valida produto selecionado
            if (!selectedProduct) {
                addToast({
                    id: 'product-required',
                    type: 'alert',
                    title: 'Produto Obrigatório',
                    description: 'Selecione um produto do pedido.',
                    time: 'Agora',
                    read: false
                });
                const productSelect = document.getElementById('product-select') as HTMLElement;
                if (productSelect) {
                    productSelect.focus();
                    productSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            // Validação de excedente para calçados
            const hasExceeded = Object.values(errors).some(e => e) || totalExceeded;

            if (hasExceeded && !showExceededWarning) {
                setShowExceededWarning(true);
                addToast({
                    id: 'warning-exceeded',
                    type: 'alert',
                    title: 'Atenção',
                    description: 'Algumas quantidades informadas são maiores que o comprado. Clique em confirmar se deseja prosseguir mesmo assim.',
                    time: 'Agora',
                    read: false
                });
                return;
            }
        }

        // Cria validQuantities baseado no tipo
        let validQuantities: { [key: string]: number } = {};

        if (isBoxSelected) {
            validQuantities = { 'volume': quantities['volume'] || 0 };
        } else {
            Object.entries(quantities).forEach(([size, qty]) => {
                if (qty > 0) validQuantities[size] = qty;
            });

            if (Object.keys(validQuantities).length === 0) {
                addToast({
                    id: 'no-size-selected',
                    type: 'alert',
                    title: 'Atenção',
                    description: 'Selecione pelo menos um tamanho com quantidade para este item.',
                    time: 'Agora',
                    read: false
                });
                const firstSizeInput = document.querySelector('.sizeInput') as HTMLInputElement;
                if (firstSizeInput) {
                    firstSizeInput.focus();
                    firstSizeInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }
        }

        if (editingItemId) {
            const updatedItems = data.items.map(item => {
                if (item.id === editingItemId) {
                    return {
                        ...item,
                        productRef: selectedProduct?.reference || 'CAIXA',
                        productDesc: selectedProduct?.description || 'CAIXA (EMBALAGEM)',
                        ca: selectedProduct?.ca ?? undefined,
                        batch: isBoxSelected ? '' : batch.filter(b => b).join(', '),
                        observation,
                        quantities: validQuantities,
                        originalQuantities: selectedProduct?.sizes || {},
                        totalPurchased: selectedProduct?.totalPurchased || 0
                    } as ComplaintItem;
                }
                return item;
            });
            updateData({ items: updatedItems });
        } else {
            const newItem: ComplaintItem = {
                id: Date.now().toString(),
                productId: selectedProduct?.productId || 0,
                productRef: selectedProduct?.reference || 'CAIXA',
                productDesc: selectedProduct?.description || 'CAIXA (EMBALAGEM)',
                ca: selectedProduct?.ca ?? undefined,
                batch: isBoxSelected ? '' : (batch.filter(b => b).join(', ')),
                observation,
                quantities: validQuantities,
                originalQuantities: selectedProduct?.sizes || {},
                totalPurchased: selectedProduct?.totalPurchased || 0
            };
            updateData({ items: [...data.items, newItem] });
        }

        resetForm();
    };

    const resetForm = () => {
        setSelectedProduct(null);
        setIsBoxSelected(false);
        setQuantities({});
        setBatch(['']);
        setObservation('');
        setEditingItemId(null);
        setShowExceededWarning(false);
    };

    const handleEditItem = (item: ComplaintItem) => {
        // Find original product details to populate the form (especially the grid limits)
        // We match by reference since we don't store productId in ComplaintItem currently
        const originalProduct = availableProducts.find(p => p.reference === item.productRef);

        if (originalProduct) {
            setSelectedProduct(originalProduct);
            setIsBoxSelected(false);
            setBatch(item.batch ? item.batch.split(', ').filter(b => b.trim()) : ['']);
            setObservation(item.observation);
            setQuantities(item.quantities);
            setEditingItemId(item.id);
            setShowExceededWarning(false);
        } else if (item.productRef === 'CAIXA' || item.productDesc === 'CAIXA (EMBALAGEM)') {
            // Handle box editing
            setSelectedProduct(null);
            setIsBoxSelected(true);
            setBatch(['']);
            setObservation(item.observation);
            setQuantities(item.quantities);
            setEditingItemId(item.id);
            setShowExceededWarning(false);
        } else {
            addToast({
                id: 'edit-error',
                type: 'alert',
                title: 'Erro no Produto',
                description: 'Erro ao carregar detalhes do produto original. Ele pode não estar mais disponível no pedido.',
                time: 'Agora',
                read: false
            });
        }
    };

    const handleRemoveItem = (itemId: string) => {
        if (editingItemId === itemId) {
            resetForm();
        }
        updateData({ items: data.items.filter(i => i.id !== itemId) });
    };

    const getExceededMessage = () => {
        if (!selectedProduct) return '';

        const messages: string[] = [];

        const exceededSizes = Object.keys(errors)
            .filter(size => errors[size])
            .map(size => {
                const purchased = selectedProduct.sizes[size] || 0;
                const alreadyClaimed = otherItemsClaimed.sizes[size] || 0;
                const available = Math.max(0, purchased - alreadyClaimed);

                if (alreadyClaimed > 0) {
                    return `Tam. ${size} (Disponível: ${available} de ${purchased})`;
                }
                return `Tam. ${size} (Comprado: ${purchased})`;
            });

        if (exceededSizes.length > 0) {
            if (exceededSizes.length === 1) {
                messages.push(`O ${exceededSizes[0]} foi excedido.`);
            } else {
                messages.push(`Os tamanhos ${exceededSizes.join(', ')} foram excedidos.`);
            }
        }

        if (totalExceeded && messages.length === 0) {
            messages.push(`A quantidade total para este produto (${currentTotal + otherItemsClaimed.total}) excede o total comprado (${selectedProduct.totalPurchased}).`);
        }

        return messages.join(' ');
    };

    return (
        <div className={styles.container}>

            {/* ── Tipo de Ressarcimento ── sempre visível no topo */}
            <div className={styles.refundSection}>
                <div className={styles.refundSectionHeader}>
                    <span className={styles.refundSectionTitle}>Intenção do cliente</span>
                    {data.tipoRessarcimento && data.tipoRessarcimento !== 'NENHUM' && (
                        <span className={styles.refundBadge}>{currentRefundLabel}</span>
                    )}
                </div>
                <p className={styles.refundSectionHint}>Selecione o que o cliente deseja como ressarcimento. <strong>Obrigatório</strong> para avançar.</p>
                <div className={styles.refundOptions}>
                    {refundOptions.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`${styles.refundOptionBtn} ${data.tipoRessarcimento === opt.value ? styles.refundOptionSelected : ''}`}
                            onClick={() => updateData({ tipoRessarcimento: opt.value as any })}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className={`${styles.formSection} ${editingItemId ? styles.editingForm : ''}`}>
                <h3 className={styles.sectionTitle}>
                    {editingItemId ? (
                        <>
                            Editar Item
                            <span className={styles.editBadge}>Modo Edição</span>
                        </>
                    ) : 'Adicionar item à reclamação'}
                </h3>

                {/* Seletor de produto/caixa - SEMPRE VISÍVEL */}
                <div className={styles.row}>
                    <div className={styles.fieldGroup} onFocus={(e) => {
                        if (e.target.closest('.customSelect')) {
                            setTimeout(() => e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                        }
                    }}>
                        <label className={styles.label}>Produto</label>
                        <div className={styles.selectRow}>
                            <CustomSelect
                                id="product-select"
                                options={productOptions}
                                value={isBoxSelected ? 'BOX' : (selectedProduct?.productId || '')}
                                onChange={handleProductChange}
                                placeholder={loadingProducts ? 'Carregando produtos...' : 'Selecione um produto do pedido...'}
                                disabled={loadingProducts || !!editingItemId}
                                style={{ width: '100%', height: '42px' }}
                            />
                            {(selectedProduct || isBoxSelected) && (
                                <button
                                    className={styles.clearProductButton}
                                    onClick={handleClearProduct}
                                    title="Limpar seleção"
                                    style={{
                                        backgroundColor: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        color: '#64748b'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fee2e2';
                                        e.currentTarget.style.color = '#dc2626';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                                        e.currentTarget.style.color = '#64748b';
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* CAIXA: Campo direto de volume */}
                {isBoxSelected && (
                    <>
                        <div className={styles.fieldGroup} style={{ marginBottom: 16 }}>
                            <label className={styles.label}>
                                Volume (Quantidade de Caixas) <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                                type="text"
                                className={styles.input}
                                placeholder="Ex: 50"
                                name="volume-input"
                                value={quantities['volume'] || ''}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value.replace(/\D/g, '')) || 0;
                                    setQuantities({ 'volume': val });
                                }}
                                onFocus={(e) => {
                                    e.target.select();
                                    setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                                }}
                            />
                            <span className={styles.inputHint}>Informe apenas o número de caixas</span>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Descrição do problema ({observation.length}/500)</label>
                            <textarea
                                className={styles.textarea}
                                maxLength={500}
                                rows={3}
                                value={observation}
                                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100)}
                                onChange={e => setObservation(e.target.value)}
                            />
                        </div>

                        <div style={{ marginTop: 20, display: 'flex', gap: '10px' }}>
                            <button
                                className={styles.addButton}
                                onClick={handleSaveItem}
                                disabled={currentTotal === 0}
                            >
                                {editingItemId ? 'Salvar Alterações' : 'Adicionar Item à Lista'}
                            </button>
                            {editingItemId && (
                                <button
                                    className={styles.addButton}
                                    onClick={resetForm}
                                    style={{ backgroundColor: '#6b7280' }}
                                >
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </>
                )}

                {/* CALÇADO: Campos adicionais */}
                {selectedProduct && !isBoxSelected && (
                    <>
                        <div className={styles.productMetaRow}>
                            <div className={styles.infoTag}>
                                <span className={styles.tagLabel}>C.A:</span>
                                <span className={styles.tagValue}>{selectedProduct.ca || 'N/A'}</span>
                            </div>
                            <div className={styles.infoTag}>
                                <span className={styles.tagLabel}>Qtd. Comprada:</span>
                                <span className={styles.tagValue}>{selectedProduct.totalPurchased}</span>
                            </div>
                            <div className={`${styles.infoTag} ${currentTotal > 0 ? styles.activeTag : ''}`}>
                                <span className={styles.tagLabel}>Qtd. Reclamada:</span>
                                <span className={styles.tagValue}>{currentTotal}</span>
                            </div>
                        </div>

                        {/* Visual Progress Bar */}
                        <div className={styles.progressContainer}>
                            <div className={styles.progressBarWrapper}>
                                <div
                                    className={styles.progressBar}
                                    style={{
                                        width: `${Math.min((currentTotal / selectedProduct.totalPurchased) * 100, 100)}%`,
                                        backgroundColor: totalExceeded ? '#ef4444' : currentTotal > 0 ? 'var(--color-primary)' : '#cbd5e1'
                                    }}
                                />
                            </div>
                            {totalExceeded && (
                                <div className={styles.progressLabel}>
                                    <span className={styles.exceededBadge}>Excedido!</span>
                                </div>
                            )}
                        </div>

                        {/* CALÇADO: Grid de Tamanhos */}
                        <div className={styles.gridContainer}>
                            <label className={styles.label} style={{ marginBottom: 8, display: 'block' }}>Grade de tamanhos (disponíveis no pedido)</label>
                            <div className={styles.sizeGrid}>
                                {Object.keys(selectedProduct.sizes)
                                    .filter(size => selectedProduct.sizes[size] > 0)
                                    .sort((a, b) => Number(a) - Number(b))
                                    .map(size => {
                                        const purchased = selectedProduct.sizes[size];
                                        const isError = errors[size];
                                        return (
                                            <div key={size} className={styles.gridCell} title={`Comprado: ${purchased}`}>
                                                <span className={styles.sizeLabel}>{size}</span>
                                                <input
                                                    type="text"
                                                    className={`${styles.sizeInput} ${isError ? styles.errorInput : ''}`}
                                                    placeholder={focusedSize === size ? "" : "0"}
                                                    value={quantities[size] || ''}
                                                    onFocus={(e) => {
                                                        setFocusedSize(size);
                                                        e.target.select();
                                                    }}
                                                    onBlur={() => setFocusedSize(null)}
                                                    onChange={(e) => handleQuantityChange(size, e.target.value)}
                                                />
                                                <span className={styles.purchasedQty}>
                                                    Comprado: {purchased}
                                                </span>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>

                        {/* LOTE: Campo de lote/fabricação */}
                        <div style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <label className={styles.label} style={{ margin: 0 }}>Lote / Fabricação <span style={{ color: '#ef4444' }}>*</span></label>
                                <span
                                    style={{
                                        fontSize: '0.7rem',
                                        color: batch.length >= 10 ? '#ef4444' : '#64748b',
                                        fontWeight: 700,
                                        backgroundColor: '#f1f5f9',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        border: '1px solid #e2e8f0'
                                    }}
                                >
                                    {batch.length}/10
                                </span>
                            </div>
                            <div className={styles.batchInputs}>
                                {batch.map((batchValue, index) => (
                                    <div key={index} className={styles.batchInputRow}>
                                        <input
                                            name={`batch-input-${index}`}
                                            type="text"
                                            className={`${styles.input} ${styles.batchInput}`}
                                            placeholder="MM.AA"
                                            value={batchValue}
                                            maxLength={5}
                                            onFocus={(e) => {
                                                e.target.select();
                                                setIsBatchFocused(true);
                                                setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                                            }}
                                            onBlur={() => setIsBatchFocused(false)}
                                            onChange={handleBatchChange(index)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addBatchField();
                                                    // Opcional: focar no novo campo após um pequeno delay para o React renderizar
                                                    setTimeout(() => {
                                                        const nextInput = document.getElementsByName(`batch-input-${index + 1}`)[0] as HTMLInputElement;
                                                        if (nextInput) nextInput.focus();
                                                    }, 50);
                                                }
                                            }}
                                        />
                                        {index > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => removeBatch(index)}
                                                className={styles.removeBatchButton}
                                                aria-label="Remover lote"
                                                tabIndex={-1}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={addBatchField}
                                className={styles.batchAddButton}
                                disabled={batch.length >= 10}
                                style={{ marginTop: '8px' }}
                            >
                                <AddIcon fontSize="small" />
                                <span>
                                    {batch.length >= 10
                                        ? 'Limite de 10 lotes atingido'
                                        : isBatchFocused
                                            ? 'Adicionar mais lotes (Enter)'
                                            : 'Adicionar mais lotes'}
                                </span>
                            </button>
                            <span className={styles.inputHint} style={{ display: 'block', marginTop: '8px' }}>Formato: mês.ano (Ex: 03.26)</span>
                        </div>

                        <div className={styles.fieldGroup} style={{ marginTop: '24px' }}>
                            <label className={styles.label}>Descrição do problema ({observation.length}/500)</label>
                            <textarea
                                className={styles.textarea}
                                maxLength={500}
                                rows={2}
                                value={observation}
                                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100)}
                                onChange={e => setObservation(e.target.value)}
                            />
                        </div>

                        {showExceededWarning && (
                            <div className={styles.warningBanner}>
                                <span className={styles.warningIcon}>⚠️</span>
                                <div className={styles.warningText}>
                                    <p><strong>Atenção:</strong> {getExceededMessage()}</p>
                                    <p>Deseja prosseguir com as quantidades informadas?</p>
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 16, display: 'flex', gap: '10px' }}>
                            <button
                                className={`${styles.addButton} ${showExceededWarning ? styles.confirmButton : ''}`}
                                onClick={handleSaveItem}
                                disabled={currentTotal === 0}
                            >
                                {showExceededWarning
                                    ? 'Sim, Confirmar e Adicionar'
                                    : (editingItemId ? 'Salvar Alterações' : 'Adicionar Calçado à Lista')}
                            </button>

                            {editingItemId && (
                                <button
                                    className={styles.addButton}
                                    onClick={resetForm}
                                    style={{ backgroundColor: '#6b7280' }}
                                >
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* List of Added Items */}
            {data.items.length > 0 && (
                <div className={styles.itemsList}>
                    <h3 className={styles.sectionTitle}>Itens Adicionados ({data.items.length})</h3>
                    {data.items.map(item => (
                        <ItemCard
                            key={item.id}
                            item={item}
                            onEdit={handleEditItem}
                            onDelete={handleRemoveItem}
                            isEditing={editingItemId === item.id}
                        />
                    ))}
                </div>
            )}

            <NotificationToastContainer
                toasts={toasts}
                onClose={removeToast}
                onClick={() => { }}
            />
        </div>
    );
};

export default ItemSelectionStep;
